import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import {
  RequestOtpResponseSchema,
  VerifyOtpResponseSchema,
  LoginResponseSchema,
  type RequestOtp,
  type VerifyOtp,
  type Login,
  type ChangePassword,
} from '@web/shared';
import { apiRequest } from '@/lib/api';
import { setSession, setToken, updateSessionUser, useSession } from '@/lib/session';

export function useRequestOtp() {
  return useMutation({
    mutationFn: (body: RequestOtp) =>
      apiRequest('/api/auth/request-otp', {
        method: 'POST',
        body,
        schema: RequestOtpResponseSchema,
        auth: false,
      }),
  });
}

/** Exchange a code for a session. Pass `password` to finish signup or reset. */
export function useVerifyOtp() {
  return useMutation({
    mutationFn: (body: VerifyOtp) =>
      apiRequest('/api/auth/verify-otp', {
        method: 'POST',
        body,
        schema: VerifyOtpResponseSchema,
        auth: false,
      }),
    onSuccess: (res) => setSession(res.token, res.user),
  });
}

/** Everyday email + password login. */
export function useLogin() {
  return useMutation({
    mutationFn: (body: Login) =>
      apiRequest('/api/auth/login', {
        method: 'POST',
        body,
        schema: LoginResponseSchema,
        auth: false,
      }),
    onSuccess: (res) => setSession(res.token, res.user),
  });
}

/** Set or change the password while signed in. Re-issues this session's token. */
export function useChangePassword() {
  const { user } = useSession();
  return useMutation({
    mutationFn: (body: ChangePassword) =>
      apiRequest('/api/auth/change-password', {
        method: 'POST',
        body,
        schema: z.object({ ok: z.literal(true), token: z.string() }),
      }),
    onSuccess: (res) => {
      setToken(res.token);
      if (user) updateSessionUser({ ...user, hasPassword: true });
    },
  });
}

/** Best-effort server logout — the caller clears local session state regardless. */
export function logoutRequest(): Promise<unknown> {
  return apiRequest('/api/auth/logout', {
    method: 'POST',
    schema: z.object({ ok: z.boolean() }),
  }).catch(() => undefined);
}
